import React, { useCallback } from 'react';
import { Theme } from '@app/theme';
import { useTranslation } from 'react-i18next';
import { createUseStyles, useTheme } from 'react-jss';
import { FieldChoice } from 'queries';
import { IconButtonGroup, TooltipText } from '@app/core';
import { LogicJumpOption } from './interface';
import { LogicButton } from './LogicButton';

const useStyles = createUseStyles(() => ({
  buttonGroup: {
    display: 'flex',
  },
}));

interface Props {
  onLogicJumpOptionChange?: (
    logicJumpOption: LogicJumpOption,
    choiceRef: string,
  ) => any;
  logicJumpOptions?: LogicJumpOption[];
  selectedLogicJumpOption?: LogicJumpOption;

  choice: FieldChoice;
  accepted: boolean;
  declined: boolean;
  tolerated: boolean;
  onDeclineClick?: (choice: FieldChoice) => any;
  onAcceptClick?: (choice: FieldChoice) => any;
  onTolerateClick?: (choice: FieldChoice) => any;
  mustChooseValidation?: boolean;
}

export const LogicAndValidationChoiceButtons = ({
  onLogicJumpOptionChange,
  logicJumpOptions,
  selectedLogicJumpOption,
  choice,
  onAcceptClick,
  onDeclineClick,
  onTolerateClick,
  accepted,
  declined,
  tolerated,
  mustChooseValidation,
}: Props) => {
  const theme: Theme = useTheme();
  const classes = useStyles({ theme });
  const { t } = useTranslation();

  const _onDeclineClick = useCallback(() => {
    if (!onDeclineClick || (mustChooseValidation && declined)) return;
    onDeclineClick(choice);
  }, [onDeclineClick, mustChooseValidation, declined, choice]);

  const _onAcceptClick = useCallback(() => {
    if (!onAcceptClick || (mustChooseValidation && accepted)) return;
    onAcceptClick(choice);
  }, [onAcceptClick, mustChooseValidation, accepted, choice]);

  const _onTolerateClick = useCallback(() => {
    if (!onTolerateClick || (mustChooseValidation && tolerated)) return;
    onTolerateClick(choice);
  }, [onTolerateClick, mustChooseValidation, tolerated, choice]);

  return (
    <>
      {!!onLogicJumpOptionChange && (
        <LogicButton
          onChange={onLogicJumpOptionChange}
          logicJumpOptions={logicJumpOptions}
          selectedLogicJumpOption={selectedLogicJumpOption}
          choiceRef={choice.ref}
        />
      )}
      <div className={classes.buttonGroup}>
        {!!onAcceptClick && (
          <TooltipText
            placement="top"
            title={t(
              'screeningForm.create.questionBuilder.choiceList.criteria.qualify',
            )}
          >
            <IconButtonGroup
              small
              icon="ri-check-line"
              color="green"
              active={accepted}
              onClick={_onAcceptClick}
              aria-label={t(
                'screeningForm.create.questionBuilder.choiceList.criteria.qualify',
              )}
            />
          </TooltipText>
        )}

        {!!onTolerateClick && (
          <TooltipText
            placement="top"
            title={t(
              'screeningForm.create.questionBuilder.choiceList.criteria.tolerate',
            )}
          >
            <IconButtonGroup
              small
              icon="ri-subtract-line"
              color="yellow"
              active={tolerated}
              onClick={_onTolerateClick}
              aria-label={t(
                'screeningForm.create.questionBuilder.choiceList.criteria.tolerate',
              )}
            />
          </TooltipText>
        )}
        {!!onDeclineClick && (
          <TooltipText
            placement="top"
            title={t(
              'screeningForm.create.questionBuilder.choiceList.criteria.disqualify',
            )}
          >
            <IconButtonGroup
              small
              icon="ri-close-line"
              color="red"
              active={declined}
              onClick={_onDeclineClick}
              aria-label={t(
                'screeningForm.create.questionBuilder.choiceList.criteria.disqualify',
              )}
            />
          </TooltipText>
        )}
      </div>
    </>
  );
};
